import { useMemo, useState } from 'react'
import { BADGE_BASE, CHIP_ACTIVE, CHIP_BASE, CHIP_INACTIVE } from '../../layout/mobile/mobileTokens'
import { orderStatusColor as STATUS_BADGE } from '../../../lib/statusStyles'
import {
  LEAD_TIME_BADGE,
  LEAD_TIME_LABEL,
  LEAD_TIME_TARGET,
  getLeadTimeStatus,
  leadTimeFulfillmentIndex,
  resolveExpectedArrival,
} from '../../../lib/leadTime'
import type { OrderStatus, PurchaseOrderWithItems } from '../../../types'
import type { RupturaForecast } from '../../../lib/rupturaForecast'
import RupturaForecastCard from '../RupturaForecastCard'

const STATUS_ORDER: OrderStatus[] = ['Pendente', 'Enviado', 'Recebido', 'Cancelado']
const fmtMoney = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const fmtDate = (iso: string | null) => iso ? new Date(iso).toLocaleDateString('pt-BR') : '—'

const leadInput = (o: PurchaseOrderWithItems) => ({
  status: o.status,
  estimated_arrival: o.estimated_arrival,
  received_at: o.received_at,
  sent_at: o.sent_at,
  lead_time_days: o.suppliers?.lead_time_days ?? null,
})

interface Props {
  orders: PurchaseOrderWithItems[]
  onSelect: (order: PurchaseOrderWithItems) => void
  rupturaForecast: RupturaForecast
}

export default function MobileOrderHistory({ orders, onSelect, rupturaForecast }: Props) {
  const [filter, setFilter] = useState<OrderStatus | 'all'>('all')
  const [supplierFilter, setSupplierFilter] = useState<string | 'all'>('all')

  const supplierOptions = useMemo(() => {
    const map = new Map<string, string>()
    for (const o of orders) if (o.supplier_id && o.suppliers?.name) map.set(o.supplier_id, o.suppliers.name)
    return [...map].map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  }, [orders])

  const bySupplier = useMemo(
    () => supplierFilter === 'all' ? orders : orders.filter(o => o.supplier_id === supplierFilter),
    [orders, supplierFilter]
  )

  const filtered = useMemo(
    () => filter === 'all' ? bySupplier : bySupplier.filter(o => o.status === filter),
    [bySupplier, filter]
  )
  const counts = STATUS_ORDER.reduce<Record<OrderStatus, number>>((acc, s) => {
    acc[s] = bySupplier.filter(o => o.status === s).length
    return acc
  }, {} as Record<OrderStatus, number>)

  const leadIndex = useMemo(() => leadTimeFulfillmentIndex(orders.map(leadInput)), [orders])
  const leadPct = leadIndex.index != null ? leadIndex.index * 100 : null
  const leadMeta = LEAD_TIME_TARGET * 100

  // Pedidos a caminho (Enviado) e a próxima data prevista de chegada.
  const emTransito = useMemo(() => {
    const enviados = orders.filter(o => o.status === 'Enviado')
    const etas = enviados
      .map(o => resolveExpectedArrival(leadInput(o)))
      .filter((d): d is Date => d != null)
      .sort((a, b) => a.getTime() - b.getTime())
    return {
      count: enviados.length,
      next: etas[0] ?? null,
      atrasados: enviados.filter(o => getLeadTimeStatus(leadInput(o)) === 'em_atraso').length,
    }
  }, [orders])

  return (
    <div className="space-y-3">
      {/* Índice de atendimento ao lead time (meta 95% entregues na data) */}
      <div className="flex items-center justify-between bg-surface border border-line rounded-xl px-3 py-2.5">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-ink-faint">Atendimento lead time</p>
          <p className={`text-xl font-bold tabular-nums leading-tight ${
            leadPct == null ? 'text-ink-faint' : leadPct >= leadMeta ? 'text-emerald-400' : 'text-red-400'
          }`}>
            {leadPct != null ? `${leadPct.toFixed(0)}%` : '—'}
            <span className="text-[10px] font-normal text-ink-faint ml-1.5">meta {leadMeta.toFixed(0)}%</span>
          </p>
        </div>
        <p className="text-[10px] text-ink-faint text-right max-w-[45%]">
          {leadIndex.evaluated > 0
            ? `${leadIndex.onTime}/${leadIndex.evaluated} entregues na data`
            : 'sem dados'}
        </p>
      </div>
      {/* Pedidos a caminho + próxima chegada prevista */}
      {emTransito.count > 0 && (
        <div className="flex items-center justify-between bg-surface border border-line rounded-xl px-3 py-2.5">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-ink-faint">A caminho</p>
            <p className="text-xl font-bold tabular-nums leading-tight text-ink">
              {emTransito.count}
              {emTransito.atrasados > 0 && (
                <span className="text-[11px] font-semibold text-red-500 dark:text-red-300 ml-1.5">
                  {emTransito.atrasados} atrasado{emTransito.atrasados > 1 ? 's' : ''}
                </span>
              )}
            </p>
          </div>
          <p className="text-[11px] text-ink-muted text-right leading-snug">
            {emTransito.next
              ? <>Próxima chegada<br /><b className="text-ink tabular-nums">{emTransito.next.toLocaleDateString('pt-BR')}</b></>
              : 'sem previsão'}
          </p>
        </div>
      )}
      {/* Previsão de fim da ruptura conforme os pedidos a caminho chegam */}
      <RupturaForecastCard forecast={rupturaForecast} compact />
      {/* Filtro de fornecedor */}
      {supplierOptions.length > 0 && (
        <select
          value={supplierFilter}
          onChange={e => setSupplierFilter(e.target.value)}
          aria-label="Filtrar por fornecedor"
          className="w-full px-3 py-2 rounded-lg text-sm bg-sunken text-ink-muted border border-line"
        >
          <option value="all">Todos os fornecedores</option>
          {supplierOptions.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      )}

      {/* Filtros — grid de chips */}
      <div className="grid grid-cols-3 gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`${CHIP_BASE} ${filter === 'all' ? 'bg-brand-600 text-white' : 'bg-sunken text-ink-muted'} ${filter === 'all' ? CHIP_ACTIVE : CHIP_INACTIVE}`}
        >
          Todos · {bySupplier.length}
        </button>
        {STATUS_ORDER.map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`${CHIP_BASE} ${STATUS_BADGE[s]} ${filter === s ? CHIP_ACTIVE : CHIP_INACTIVE}`}
          >
            {s} {counts[s]}
          </button>
        ))}
      </div>

      {/* Lista de cards */}
      {filtered.length === 0 ? (
        <div className="py-16 text-center text-ink-faint">
          <p className="text-2xl mb-2">📦</p>
          <p>Nenhum pedido encontrado.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(o => (
            <button
              key={o.id}
              onClick={() => onSelect(o)}
              className="w-full text-left bg-surface active:bg-sunken border border-line rounded-xl p-3 touch-action-manip no-tap-highlight"
            >
              {/* Header: número + status */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <p className="text-[10px] text-ink-faint uppercase tracking-wider">Pedido</p>
                  <p className="text-ink font-mono text-sm font-bold truncate">{o.order_number}</p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className={`${BADGE_BASE} ${STATUS_BADGE[o.status]}`}>{o.status}</span>
                  {(() => {
                    const st = getLeadTimeStatus(leadInput(o))
                    if (st === 'na') return null
                    return <span className={`${BADGE_BASE} ${LEAD_TIME_BADGE[st]}`}>{LEAD_TIME_LABEL[st]}</span>
                  })()}
                </div>
              </div>

              {/* Fornecedor (linha grande) */}
              <p className="text-ink text-sm font-semibold truncate leading-tight mb-2" title={o.suppliers?.name ?? ''}>
                {o.suppliers?.name ?? '—'}
              </p>

              {/* Previsão de chegada (ETA) — só para pedidos a caminho */}
              {(o.status === 'Pendente' || o.status === 'Enviado') && (() => {
                const eta = resolveExpectedArrival(leadInput(o))
                if (!eta) return null
                const late = getLeadTimeStatus(leadInput(o)) === 'em_atraso'
                return (
                  <p className={`text-[11px] mb-2 flex items-center gap-1.5 ${late ? 'text-red-600 dark:text-red-300' : 'text-ink-muted'}`}>
                    <span aria-hidden>📅</span>
                    Previsão de chegada: <b className={late ? '' : 'text-ink'}>{eta.toLocaleDateString('pt-BR')}</b>
                    {late && <span className="text-[10px] font-semibold">· atrasado</span>}
                  </p>
                )
              })()}

              {/* Métricas em grade */}
              <div className="grid grid-cols-3 gap-2 text-center bg-sunken rounded-lg p-2">
                <div>
                  <p className="text-[9px] text-ink-faint uppercase tracking-wider">Data</p>
                  <p className="text-xs text-ink font-semibold tabular-nums">{fmtDate(o.created_at)}</p>
                </div>
                <div>
                  <p className="text-[9px] text-ink-faint uppercase tracking-wider">Itens</p>
                  <p className="text-xs text-ink font-semibold tabular-nums">{o.purchase_order_items.length}</p>
                </div>
                <div>
                  <p className="text-[9px] text-ink-faint uppercase tracking-wider">Total</p>
                  <p className="text-xs text-accent font-bold tabular-nums">{fmtMoney(o.total_value)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
